import * as Menu from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@/auth/AuthProvider'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { SignInAsMenu } from '@/staff/SignInAsMenu'
import { Avatar, Icons, useToast } from '@/ui'
import { useAccountIdentity } from './identity'
import { itemClass, menuClass } from './menuStyles'
import { returnToSelf, switchError } from './impersonation'

/** Avatar button in either header; opens the account menu. `tone="dark"` for the console's dark header. */
export function AccountMenu({ tone }: { tone: 'dark' | 'light' }) {
  const { staff, impersonation, signOut } = useAuth()
  const { name, email, photo, roleLabel } = useAccountIdentity()
  const { toast } = useToast()
  const [switching, setSwitching] = useState(false)
  const main = useRef<HTMLDivElement>(null)
  const dark = tone === 'dark'
  // A phone has no room beside the menu for a side menu, so "Sign in as" opens its list inside the menu instead.
  const narrow = useMediaQuery('(max-width: 39.99rem)')
  const [signInAsOpen, setSignInAsOpen] = useState(false)

  const back = async () => {
    setSwitching(true)
    try {
      await returnToSelf()
    } catch (e) {
      setSwitching(false)
      toast(switchError(e), 'error')
    }
  }

  return (
    <Menu.Root modal={false} onOpenChange={(open) => !open && setSignInAsOpen(false)}>
      <Menu.Trigger
        aria-label={`Account: ${name}`}
        className={clsx(
          'flex min-h-9 items-center gap-2.5 py-1 pl-1 pr-1.5 outline-offset-2 transition-colors',
          dark ? 'text-paper/80 hover:text-paper data-[state=open]:text-paper' : 'text-charcoal hover:text-ink data-[state=open]:text-ink',
        )}
      >
        <Avatar name={name} src={photo} inverse={dark} />
        <span className={clsx('max-w-40 truncate whitespace-nowrap text-small', dark ? 'hidden xl:inline' : 'hidden md:inline')}>{name}</span>
        <Icons.ChevronDown size={12} className="shrink-0" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content ref={main} align="end" sideOffset={8} collisionPadding={16} className={clsx(menuClass, 'w-72 max-w-[calc(100vw-2rem)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto')}>
          <Menu.Label className="flex items-center gap-3 px-4 pb-4 pt-2">
            <Avatar name={name} src={photo} size="md" />
            <span className="min-w-0">
              <span className="block truncate font-medium text-ink">{name}</span>
              {email !== name && <span className="block truncate text-small text-dim">{email}</span>}
              <span className="mt-1 block text-[0.6875rem] uppercase tracking-eyebrow text-dim">{roleLabel}</span>
            </span>
          </Menu.Label>
          <Menu.Separator className="mb-2 h-px bg-gray-200" />
          <Menu.Item asChild className={itemClass}>
            <Link to="/account">Account settings</Link>
          </Menu.Item>
          {staff?.admin && !impersonation && narrow && (
            <>
              <Menu.Item
                aria-expanded={signInAsOpen}
                className={clsx(itemClass, 'justify-between', signInAsOpen && 'bg-gray-100 text-ink')}
                onSelect={(e) => {
                  // Opens the list below; it isn't a pick, so the menu stays open.
                  e.preventDefault()
                  setSignInAsOpen((o) => !o)
                }}
              >
                Sign in as
                <Icons.ChevronDown size={12} className={clsx('transition-transform', signInAsOpen && 'rotate-180')} />
              </Menu.Item>
              {signInAsOpen && (
                <Menu.Group className="border-y border-gray-200 bg-gray-50 py-2">
                  <SignInAsMenu />
                </Menu.Group>
              )}
            </>
          )}
          {staff?.admin && !impersonation && !narrow && (
            <Menu.Sub>
              <Menu.SubTrigger className={clsx(itemClass, 'justify-between data-[state=open]:bg-gray-100 data-[state=open]:text-ink')}>
                Sign in as
                <Icons.ChevronRight size={12} />
              </Menu.SubTrigger>
              <Menu.Portal>
                <Menu.SubContent
                  sideOffset={6}
                  alignOffset={-9}
                  collisionPadding={16}
                  // At the right edge of the screen this menu opens to the left. Radix only treats the pointer as heading
                  // into a side menu once it has seen it move that way inside the row, and it starts out assuming
                  // "right", so a straight move left often hands focus back to the main menu, which closed this one
                  // mid-move. Focus returning to the main menu is never a reason to close it; Escape, picking someone
                  // or closing the account menu still do.
                  onFocusOutside={(e) => {
                    const to = e.detail.originalEvent.target
                    if (to instanceof Node && main.current?.contains(to)) e.preventDefault()
                  }}
                  className={clsx(menuClass, 'w-80 max-w-[calc(100vw-2rem)]')}
                >
                  <SignInAsMenu />
                </Menu.SubContent>
              </Menu.Portal>
            </Menu.Sub>
          )}
          <Menu.Separator className="my-2 h-px bg-gray-200" />
          {impersonation ? (
            // Signing out here would end the admin's session too; returning is the way out.
            <Menu.Item
              className={itemClass}
              disabled={switching}
              onSelect={(e) => {
                e.preventDefault()
                void back()
              }}
            >
              {switching ? 'Returning…' : `Return to ${impersonation.byEmail ?? 'your account'}`}
            </Menu.Item>
          ) : (
            <Menu.Item className={itemClass} onSelect={() => void signOut()}>
              Sign out
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}
